import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useNotifications } from "../../../context/NotificationContext";
import { formatNotificationTime } from "../../../utils/notificationStorage";
import "./NotificationBell.css";

/** Single notification panel — mount once (e.g. in NotificationProvider). */
export function NotificationPanel() {
  const {
    notifications,
    unreadCount,
    panelOpen,
    loading,
    fetchError,
    closePanel,
    markAsRead,
    markAllAsRead,
    refreshNotifications,
  } = useNotifications();

  useEffect(() => {
    if (!panelOpen) return;
    refreshNotifications();
  }, [panelOpen, refreshNotifications]);

  useEffect(() => {
    if (!panelOpen) return;

    const handleEscape = (event) => {
      if (event.key === "Escape") closePanel();
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [panelOpen, closePanel]);

  if (!panelOpen) return null;

  const stopPanelEvent = (event) => {
    event.stopPropagation();
  };

  return createPortal(
    <>
      <div
        className="notification-panel-backdrop"
        aria-hidden="true"
        onClick={closePanel}
      />
      <div
        className="notification-panel notification-panel--admin"
        role="dialog"
        aria-label="Notifications"
        onMouseDown={stopPanelEvent}
        onClick={stopPanelEvent}
      >
        <div className="notification-panel-header">
          <div>
            <h2 className="notification-panel-title">Notifications</h2>
            {unreadCount > 0 && (
              <p className="notification-panel-subtitle">
                {unreadCount} unread
              </p>
            )}
          </div>
          <div className="notification-panel-actions">
            {notifications.length > 0 && unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllAsRead}
                className="notification-panel-link-btn"
              >
                Mark all read
              </button>
            )}
            <button
              type="button"
              onClick={closePanel}
              className="notification-panel-close"
              aria-label="Close notifications"
            >
              ×
            </button>
          </div>
        </div>

        <div className="notification-panel-body">
          {loading && notifications.length === 0 ? (
            <p className="notification-panel-empty">Loading notifications…</p>
          ) : fetchError ? (
            <p className="notification-panel-empty">
              Could not load notifications.{" "}
              <button
                type="button"
                className="notification-item-link"
                onClick={() => refreshNotifications()}
              >
                Retry
              </button>
            </p>
          ) : notifications.length === 0 ? (
            <p className="notification-panel-empty">No notifications yet.</p>
          ) : (
            <ul className="notification-list">
              {notifications.map((item) => (
                <li key={`${item.recipientId}-${item.notificationId}`}>
                  <div
                    className={`notification-item ${
                      item.read ? "notification-item--read" : ""
                    }`}
                  >
                    <div className="notification-item-main">
                      <p className="notification-item-title">
                        {!item.read && (
                          <span
                            className="notification-unread-dot"
                            aria-hidden="true"
                          />
                        )}
                        {item.title}
                      </p>
                      <p className="notification-item-message">
                        {item.message}
                      </p>
                      <p className="notification-item-time">
                        {formatNotificationTime(item.createdAt)}
                      </p>
                    </div>
                    <div className="notification-item-actions">
                      <Link
                        to={item.link}
                        className="notification-item-link"
                        onClick={(e) => {
                          e.stopPropagation();
                          closePanel();
                        }}
                      >
                        View
                      </Link>
                      {!item.read && (
                        <button
                          type="button"
                          className="notification-item-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(item.recipientId);
                          }}
                        >
                          Mark read
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}

export default NotificationPanel;
